import React from 'react';
import PropTypes from 'prop-types';
import {
  Button, TockBadge, getTranslation, Icon, PeopleIcon,
} from '@resy/ui-kitchen';
import Close from '../../../../../ui-kitchen/src/svg/icons/ic_close_20x20.svg';
import toLocaleLongDate from '../../utils/tockExperienceUtils';

import './TockExperienceOptions.scss';

const TIME_FORMAT_IN = 'YYYY-MM-DD HH:mm:ss';
const TIME_FORMAT_OUT = 'h:mm A';

const formatTime = (time) => {
  if (!time) return '';
  const parsed = moment(time, TIME_FORMAT_IN);
  return parsed.isValid() ? parsed.format(TIME_FORMAT_OUT) : '';
};

const getMatchingSlots = (experience, selectedTime) => (
  (experience?.slots || []).filter((slot) => slot?.date?.start === selectedTime)
);

const getExperienceImageUrl = (experience) => {
  const image = experience?.images?.[0];
  if (typeof image === 'string') return image;
  if (image && typeof image === 'object') return image.url || image.src || null;
  return null;
};

const triggerOpenOfferingOnKeyboard = (event, callback) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    callback();
  }
};

const TockExperienceOptions = ({
  experiences,
  isMobile,
  selectedTime,
  selectedDate,
  selectedSeats,
  venueName,
  onClose,
  onBookSlotClick,
  handleOpenOffering,
}) => {
  const formattedTime = formatTime(selectedTime);
  const formattedDate = toLocaleLongDate(selectedDate);
  const selectedTimeLabel = formattedTime || selectedTime;
  const bookText = getTranslation({
    translationKey: 'book',
    fallback: 'Book',
  });
  const moreTimesText = getTranslation({
    translationKey: 'more_times',
    fallback: 'More Times',
  });
  const closeLabel = getTranslation({
    translationKey: 'close',
    fallback: 'Close',
  });

  const matchingExperiences = experiences || [];

  return (
    <div className="TockExperienceOptions" data-test-id="tock-experience-options">
      <div className="TockExperienceOptions__title-bar">
        <div className="TockExperienceOptions__title-bar-inner">
          <Button
            onClick={onClose}
            variant="no-style"
            className="TockExperienceOptions__close"
            data-test-id="tock-experience-options-button-close"
            aria-label={closeLabel}
            autoFocus
          >
            <span aria-hidden="true">
              <Close aria-hidden="true" focusable="false" />
            </span>
          </Button>

          {venueName && (
            <h3 className="TockExperienceOptions__venue-name">{venueName}</h3>
          )}
          <div className="TockExperienceOptions__divider" />
        </div>
      </div>

      <div className="TockExperienceOptions__content">
        <div className="TockExperienceOptions__header">
          <h2 className="TockExperienceOptions__title">
            {getTranslation({ translationKey: 'tock_experience_options_title', fallback: `Choose a ${formattedTime} Option`, translationValues: { time: formattedTime } })}
          </h2>
          <TockBadge large />
          <div className="TockExperienceOptions__meta">
            <span className="TockExperienceOptions__sr-only">
              {getTranslation({
                translationKey: 'tock_experience_options_party_label',
                fallback: `Party of ${selectedSeats}, ${formattedDate}, ${formattedTime}`,
                translationValues: { seats: String(selectedSeats), date: formattedDate, time: formattedTime },
              })}
            </span>
            <span aria-hidden="true" className="TockExperienceOptions__meta-visual">
              <Icon component={PeopleIcon} name="people" height={15} width={15} ariaHidden="true" />
              <span className="TockExperienceOptions__meta-text">
                {selectedSeats}
              </span>
              <span className="TockExperienceOptions__meta-separator" aria-hidden="true">•</span>
              <span className="TockExperienceOptions__meta-text">{formattedDate}</span>
            </span>
          </div>
        </div>
        <div className="TockExperienceOptions__body">
          {matchingExperiences.map((experience) => {
            const matchingSlots = getMatchingSlots(experience, selectedTime);
            const imageUrl = getExperienceImageUrl(experience);
            const hasNoMatchingSlots = matchingSlots.length === 0;
            const isSingleUnlabeledSlot = matchingSlots.length === 1
            && !matchingSlots[0]?.config?.type;
            let bookingCtaButtons;

            if (hasNoMatchingSlots) {
              bookingCtaButtons = (
                <Button
                  className="TockExperienceOptions__card-cta"
                  variant="primary"
                  disabled
                  data-test-id={`tock-experience-options-button-book-${experience.id || experience.name}-selected-time`}
                >
                  {selectedTimeLabel}
                </Button>
              );
            } else if (isSingleUnlabeledSlot) {
              bookingCtaButtons = (
                <Button
                  className="TockExperienceOptions__card-cta"
                  variant="primary"
                  onClick={() => onBookSlotClick(matchingSlots[0].tock_booking_url)}
                  data-test-id={`tock-experience-options-button-book-${experience.id || experience.name}`}
                >
                  {bookText}
                </Button>
              );
            } else {
              bookingCtaButtons = matchingSlots.map((slot) => (
                <Button
                  key={slot?.tock_booking_url}
                  className="TockExperienceOptions__card-cta"
                  variant="primary"
                  onClick={() => onBookSlotClick(slot.tock_booking_url)}
                  data-test-id={`tock-experience-options-button-book-${experience.id || experience.name}-${slot?.tock_booking_url}`}
                >
                  {slot?.config?.type || bookText}
                </Button>
              ));
            }

            return (
              <div
                className="TockExperienceOptions__card"
                key={experience.id || experience.name}
                data-test-id={`tock-experience-options-card-${experience.id || experience.name}`}
              >
                <div className="TockExperienceOptions__card-about">
                  <div className="TockExperienceOptions__card-body">
                    <div
                      className="TockExperienceOptions__card-info"
                      role="button"
                      tabIndex={0}
                      onClick={() => handleOpenOffering(experience, isMobile)}
                      onKeyDown={(event) => triggerOpenOfferingOnKeyboard(
                        event,
                        () => handleOpenOffering(experience, isMobile)
                      )}
                    >
                      <h3 className="TockExperienceOptions__card-name">{experience.name}</h3>
                      {experience.price_type_display && (
                      <p className="TockExperienceOptions__card-price">
                        {experience.price_type_display}
                      </p>
                      )}
                      {experience.description && (
                      <p className="TockExperienceOptions__card-description">
                        {experience.description}
                      </p>
                      )}
                    </div>
                  </div>
                  {imageUrl && (
                  <div
                    className="TockExperienceOptions__card-image"
                    style={{ backgroundImage: `url(${imageUrl})` }}
                    role="button"
                    aria-label={getTranslation({ translationKey: 'tock_experience_options_more_times_label', fallback: `More times for ${experience.name}`, translationValues: { name: experience.name } })}
                    tabIndex={0}
                    onClick={() => handleOpenOffering(experience, isMobile)}
                    onKeyDown={(event) => triggerOpenOfferingOnKeyboard(
                      event,
                      () => handleOpenOffering(experience, isMobile)
                    )}
                  />
                  )}
                </div>
                <div className="TockExperienceOptions__card-ctas">
                  {bookingCtaButtons}
                  <Button
                    className="TockExperienceOptions__card-cta"
                    variant="secondary"
                    onClick={() => handleOpenOffering(experience, isMobile)}
                    data-test-id={`tock-experience-options-button-more-times-${experience.id || experience.name}`}
                    aria-label={getTranslation({ translationKey: 'tock_experience_options_more_times_label', fallback: `More times for ${experience.name}`, translationValues: { name: experience.name } })}
                  >
                    {moreTimesText}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

TockExperienceOptions.displayName = 'TockExperienceOptions';

TockExperienceOptions.propTypes = {
  experiences: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    description: PropTypes.string,
    images: PropTypes.arrayOf(PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        url: PropTypes.string,
        src: PropTypes.string,
      }),
    ])),
    price_type_display: PropTypes.string,
    slots: PropTypes.arrayOf(PropTypes.shape({
      config: PropTypes.shape({
        type: PropTypes.string,
      }),
      date: PropTypes.shape({
        start: PropTypes.string,
      }),
      tock_booking_url: PropTypes.string,
    })),
  })),
  isMobile: PropTypes.bool,
  selectedTime: PropTypes.string,
  selectedDate: PropTypes.string,
  selectedSeats: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  venueName: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onBookSlotClick: PropTypes.func.isRequired,
  handleOpenOffering: PropTypes.func,
};

TockExperienceOptions.defaultProps = {
  experiences: [],
  isMobile: false,
  selectedTime: null,
  selectedDate: null,
  selectedSeats: null,
  venueName: null,
  handleOpenOffering: () => {},
};

export default TockExperienceOptions;
